import { setSentryUser } from '../utils/sentry';

// Utiliser la variable d'environnement ou fallback vers localhost
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/';

class AuthService {
    async login(email, password) {
        try {
            const response = await fetch(`${API_URL}/login`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/ld+json, application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status} : accès non autorisé. Veuillez vous reconnecter.`);
            }

            const data = await response.json();
            const user = data.user;
            
            // Stocker les informations utilisateur dans localStorage
            localStorage.setItem('user', JSON.stringify(user));
            setSentryUser(user);

            return user;
        } catch (error) {
            console.error('Erreur de connexion:', error);
            throw error;
        }
    }

    logout() {
        localStorage.removeItem('user');
        setSentryUser(null);
    }

    /**
     * Resynchronise l'utilisateur local depuis la session serveur (/api/me).
     * C'est le garde-fou anti-usurpation : le localStorage peut être trafiqué
     * dans l'inspecteur, mais l'identité affichée est ici REMPLACÉE par celle
     * que le serveur reconnaît réellement (issue du cookie de session, que le
     * JavaScript ne peut ni lire ni forger).
     *   - 200 : on écrase le localStorage avec la vérité serveur.
     *   - 401 : session invalide/absente → on déconnecte localement.
     *   - erreur réseau : on garde le cache local (pas de déconnexion brutale).
     */
    async syncSession() {
        try {
            const response = await fetch(`${API_URL}/me`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Accept': 'application/json' },
            });
            if (response.status === 401) {
                this.logout();
                return null;
            }
            if (!response.ok) {
                return this.getCurrentUser();
            }
            const data = await response.json();
            const user = data.user;
            localStorage.setItem('user', JSON.stringify(user));
            setSentryUser(user);
            return user;
        } catch (error) {
            // API injoignable (hors-ligne, coupure) : on n'expulse pas l'utilisateur.
            return this.getCurrentUser();
        }
    }

    getCurrentUser() {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    }

    isAuthenticated() {
        return this.getCurrentUser() !== null;
    }

    isSuperadmin() {
        const user = this.getCurrentUser();
        return !!(user && user.grade && user.grade.id === 5);
    }

    hasPermission(permissionName) {
        const user = this.getCurrentUser();
        if (!user) return false;
        const lists = [
            user.role?.permissions,
            user.grade?.permissions,
            user.permissions,
        ];
        return lists.some((list) =>
            Array.isArray(list) && list.some((permission) => permission.nom === permissionName)
        );
    }

    /** Extra fiche ou grade (pas le rôle) — conservé pour Evolis « toutes entités ». */
    hasUserPermission(permissionName) {
        const user = this.getCurrentUser();
        if (!user) return false;
        const lists = [user.permissions, user.grade?.permissions];
        return lists.some((list) =>
            Array.isArray(list) && list.some((permission) => permission.nom === permissionName)
        );
    }

    isAdmin() {
        const user = this.getCurrentUser();
        if (!user) return false;
        if (this.hasPermission('admin_ecriture')) return true;
        const id = user.grade?.id;
        return id === 4 || id === 5;
    }

    /** Interrupteur présence toutes entités (permission, fallback Superadmin). */
    peutPresenceToutes() {
        return this.hasPermission('presence_toutes_entites') || this.isSuperadmin();
    }
}

export default new AuthService(); 